// Bandeja de "Pedidos web": lo que mandaron los locales desde el celular y
// todavia no confirmo administracion.
//
// Un pedido PENDIENTE no existe todavia para la cuadra ni para la cuenta
// corriente. Al confirmarlo se congelan los precios, entra a la produccion del
// dia y se genera la COMPRA — y recien ahi sale el remito.

import { api } from "../api";
import { formatearPesos } from "../../lib/money";
import { abrirRemito } from "../remito";
import { alLlegarAviso } from "../notificaciones";
import { iconoTilde, iconoTacho } from "../iconos";
import { nombreTurno } from "../../lib/types";
import type { Pedido } from "../../lib/types";

type PedidoFila = Pedido & { cliente_nombre: string; cliente_codigo: string };

interface DetalleResp {
  lineas: Array<{ cantidad: number; descripcion: string; subtotal: number }>;
}

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

export async function renderPedidosWeb(contenedor: HTMLElement): Promise<void> {
  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina">Pedidos web</h1>
      <p class="subtitulo">Pedidos que mandaron los locales. Hasta que no los confirmes no entran a producción ni a la cuenta.</p>
    </div>
    <p id="nuevos" class="mb-3 hidden rounded-xl bg-acento-50 px-4 py-2 text-sm font-medium text-acento-800"></p>
    <div id="lista" class="space-y-3"><div class="tarjeta text-center text-masa-400">Cargando...</div></div>
    <dialog id="dlg"></dialog>
    <dialog id="dlgRemito" class="p-0"></dialog>`;

  const lista = contenedor.querySelector("#lista") as HTMLElement;
  const cartel = contenedor.querySelector("#nuevos") as HTMLElement;
  const dlg = contenedor.querySelector("#dlg") as HTMLDialogElement;
  const dlgRemito = contenedor.querySelector("#dlgRemito") as HTMLDialogElement;

  // Ids que ya estaban pintados: sirve para saber cuales son NUEVOS de verdad
  // (confirmar o rechazar uno cambia la lista, pero no es una novedad).
  let vistos = new Set<number>();
  let avisoTimer = 0;

  function avisarNuevos(cuantos: number): void {
    cartel.textContent =
      cuantos === 1 ? "Llego 1 pedido nuevo" : `Llegaron ${cuantos} pedidos nuevos`;
    cartel.classList.remove("hidden");
    window.clearTimeout(avisoTimer);
    avisoTimer = window.setTimeout(() => cartel.classList.add("hidden"), 5000);
  }

  async function cargar(): Promise<void> {
    let pedidos: PedidoFila[];
    try {
      pedidos = await api.get<PedidoFila[]>("/api/pedidos?estado=PENDIENTE");
    } catch (e) {
      lista.innerHTML = `<div class="tarjeta text-red-700">${esc(
        e instanceof Error ? e.message : "Error",
      )}</div>`;
      return;
    }
    vistos = new Set(pedidos.map((p) => p.id));

    if (pedidos.length === 0) {
      lista.innerHTML = `<div class="tarjeta text-center text-masa-400">No hay pedidos web esperando confirmación.</div>`;
      return;
    }

    lista.innerHTML = pedidos
      .map(
        (p) => `
        <div class="tarjeta" data-fila="${p.id}">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div class="text-lg font-bold">${esc(p.cliente_nombre)}</div>
              <div class="text-xs text-masa-400">${esc(p.cliente_codigo)} · pedido #${esc(p.id)} · entrega ${esc(p.fecha_entrega)}</div>
            </div>
            <span class="flex items-center gap-1">
              <span class="rounded-full bg-crema-200 px-2.5 py-1 text-xs font-bold text-masa-700">${esc(nombreTurno(p.turno))}</span>
              <span class="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800">Pendiente</span>
            </span>
          </div>
          <div id="det-${p.id}" class="mt-2 text-sm text-masa-500">Cargando artículos...</div>
          <div class="mt-3 flex flex-wrap items-center justify-between gap-2">
            <span class="text-lg font-bold tabular-nums">${formatearPesos(p.total)}</span>
            <span class="flex gap-2">
              <button class="btn-sec" data-rechazar="${p.id}">${iconoTacho} Rechazar</button>
              <button class="btn-primario" data-confirmar="${p.id}">${iconoTilde} Confirmar</button>
            </span>
          </div>
          <p class="mt-1 text-sm text-red-600" data-err="${p.id}"></p>
        </div>`,
      )
      .join("");

    // El detalle se pide aparte para que la bandeja aparezca enseguida.
    for (const p of pedidos) {
      void api
        .get<DetalleResp>(`/api/pedidos/${p.id}`)
        .then((r) => {
          const caja = lista.querySelector(`#det-${p.id}`);
          if (!caja) return;
          caja.innerHTML = r.lineas
            .map(
              (l) =>
                `<span class="mr-3 inline-block whitespace-nowrap"><b>${esc(l.cantidad)}</b> ${esc(l.descripcion)}</span>`,
            )
            .join("");
        })
        .catch(() => {
          const caja = lista.querySelector(`#det-${p.id}`);
          if (caja) caja.textContent = "No se pudo cargar el detalle.";
        });
    }
  }

  lista.addEventListener("click", async (ev) => {
    const objetivo = ev.target as HTMLElement;

    const confirmar = objetivo.closest("[data-confirmar]");
    if (confirmar) {
      const id = Number(confirmar.getAttribute("data-confirmar"));
      const err = lista.querySelector(`[data-err="${id}"]`) as HTMLElement;
      const boton = confirmar as HTMLButtonElement;
      boton.disabled = true;
      err.textContent = "";
      try {
        await api.post(`/api/pedidos/${id}/confirmar`);
        await cargar();
        // Recien confirmado ya tiene precios congelados: sale el remito.
        await abrirRemito(dlgRemito, id);
      } catch (e) {
        boton.disabled = false;
        err.textContent = e instanceof Error ? e.message : "Error";
      }
      return;
    }

    const rechazar = objetivo.closest("[data-rechazar]");
    if (rechazar) {
      const id = Number(rechazar.getAttribute("data-rechazar"));
      dlg.innerHTML = `
        <div class="tarjeta w-[24rem] max-w-full border-0 shadow-none">
          <h2 class="mb-2 text-lg font-bold">Rechazar el pedido #${esc(id)}</h2>
          <p class="text-sm text-masa-600">El pedido queda anulado, al local le llega un <b>aviso al celular</b> y lo va a ver como cancelado. Si el motivo necesita explicación, conviene igual una llamada.</p>
          <p id="r_err" class="mt-2 text-sm text-red-600"></p>
          <div class="mt-4 flex justify-end gap-2">
            <button id="no" class="btn-sec">Volver</button>
            <button id="si" class="btn-peligro">Rechazar</button>
          </div>
        </div>`;
      const rErr = dlg.querySelector("#r_err") as HTMLElement;
      dlg.querySelector("#no")?.addEventListener("click", () => dlg.close());
      dlg.querySelector("#si")?.addEventListener("click", async () => {
        try {
          await api.del(`/api/pedidos/${id}`);
          dlg.close();
          await cargar();
        } catch (e) {
          rErr.textContent = e instanceof Error ? e.message : "Error";
        }
      });
      dlg.showModal();
    }
  });

  await cargar();

  /**
   * La bandeja se actualiza sola: llega la notificacion al celular, se abre la
   * app y el pedido tiene que estar ahi sin recargar nada.
   *
   * Tres cuidados, todos por la misma razon —que no se mueva la pantalla abajo
   * del dedo mientras alguien esta confirmando—:
   *   1. si no cambio nada, no se toca el DOM (si no parpadearia cada 60 s);
   *   2. si hay un dialogo abierto, se saltea el turno;
   *   3. el cartel de "pedido nuevo" cuenta solo los ids que NO estaban.
   */
  async function mirar(): Promise<void> {
    if (dlg.open || dlgRemito.open) return;
    let pedidos: PedidoFila[];
    try {
      pedidos = await api.get<PedidoFila[]>("/api/pedidos?estado=PENDIENTE");
    } catch {
      return; // sin conexion: se reintenta en el proximo ciclo
    }
    const ids = pedidos.map((p) => p.id);
    const nuevos = ids.filter((id) => !vistos.has(id)).length;
    const cambio = nuevos > 0 || ids.length !== vistos.size;
    if (!cambio) return;
    await cargar();
    if (nuevos > 0) avisarNuevos(nuevos);
  }

  // El router reemplaza el innerHTML sin avisarle a nadie: la pagina se da
  // cuenta sola de que ya no esta en pantalla y apaga su timer.
  const timer = window.setInterval(() => {
    if (!document.body.contains(lista)) {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", alVolver);
      return;
    }
    void mirar();
  }, 60000);

  // Volver a la app (o a la pestana) actualiza en el acto, sin esperar los 60 s.
  function alVolver(): void {
    if (document.visibilityState === "visible" && document.body.contains(lista)) void mirar();
  }
  document.addEventListener("visibilitychange", alVolver);

  // Y si llega el push del pedido nuevo, aparece en el momento: el reloj de
  // 60 s queda como red de seguridad para cuando no hay push (iPhone sin la app
  // instalada, permiso no dado, navegador que no lo soporta).
  alLlegarAviso(lista, () => void mirar());
}
